import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Headphones,
  MessageSquareWarning,
  Search,
  Download,
  MessageCircle,
  UserPlus,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const STATUSES = ["Baru", "Diproses", "Selesai", "Ditolak"];
const STATUS_COLOR = {
  Baru: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/30",
  Diproses: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/30",
  Selesai: "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/30",
  Ditolak: "bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/30",
};
const UNASSIGNED = "__unassigned";
const PAGE_SIZE = 25;

// Convert an Indonesian phone number to wa.me format (62xxxxxxxxxx)
const toWaNumber = (raw) => {
  let n = (raw || "").replace(/[^\d+]/g, "");
  if (n.startsWith("+")) n = n.slice(1);
  if (n.startsWith("0")) n = "62" + n.slice(1);
  else if (n.startsWith("8")) n = "62" + n;
  return n;
};

export default function TicketsPage() {
  const { user } = useAuth();
  const isManager = ["super_admin", "direktur", "manager"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const [page, setPage] = useState(1);

  const params = useMemo(() => {
    const p = {};
    if (typeFilter !== "all") p.type = typeFilter;
    if (statusFilter !== "all") p.status = statusFilter;
    if (q) p.q = q;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [typeFilter, statusFilter, q, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [
        api.get("/tickets", { params }),
        api.get("/tickets/stats"),
        api.get("/public/ticket-config"),
      ];
      if (isManager) reqs.push(api.get("/users"));
      const [{ data }, { data: s }, { data: cfg }, usersRes] = await Promise.all(reqs);
      setTickets(data);
      setStats(s);
      setCustomFields(cfg.custom_fields || []);
      if (usersRes) setUsers(usersRes.data);
      setSelected(new Set());
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, statusFilter, dateFrom, dateTo]);

  const userName = (id) => users.find((u) => u.id === id)?.name || users.find((u) => u.id === id)?.email || "";

  const updateStatus = async (t, status) => {
    try {
      await api.patch(`/tickets/${t.id}`, { status });
      toast.success(`Status ${t.ticket_no} → ${status}`);
      setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
      api.get("/tickets/stats").then(({ data }) => setStats(data));
    } catch (e) {
      toast.error("Gagal memperbarui status");
    }
  };

  const saveNote = async (t, note) => {
    if (note === (t.response_note || "")) return;
    try {
      await api.patch(`/tickets/${t.id}`, { response_note: note });
      toast.success(`Tanggapan ${t.ticket_no} disimpan`);
    } catch (e) {
      toast.error("Gagal menyimpan tanggapan");
    }
  };

  const assignOne = async (t, uid) => {
    const assigned_to = uid === UNASSIGNED ? null : uid;
    try {
      await api.patch(`/tickets/${t.id}`, { assigned_to });
      setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, assigned_to, assigned_to_name: assigned_to ? userName(assigned_to) : "" } : x)));
      toast.success(assigned_to ? `${t.ticket_no} → ${userName(assigned_to)}` : `${t.ticket_no} dilepas`);
    } catch (e) {
      toast.error("Gagal menugaskan tiket");
    }
  };

  const bulkAssign = async () => {
    if (selected.size === 0) return;
    const assigned_to = bulkAssignTo === UNASSIGNED ? null : bulkAssignTo || null;
    try {
      const { data } = await api.post("/tickets/assign-bulk", {
        ticket_ids: Array.from(selected),
        assigned_to,
      });
      toast.success(`${data.updated} tiket diperbarui`);
      setBulkAssignTo("");
      load();
    } catch (e) {
      toast.error("Gagal menugaskan massal");
    }
  };

  const deleteOne = async (t) => {
    if (!window.confirm(`Hapus tiket ${t.ticket_no}? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      toast.success(`${t.ticket_no} dihapus`);
      setTickets((prev) => prev.filter((x) => x.id !== t.id));
      api.get("/tickets/stats").then(({ data }) => setStats(data));
    } catch (e) {
      toast.error("Gagal menghapus tiket");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Hapus ${selected.size} tiket terpilih? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const { data } = await api.post("/tickets/delete-bulk", { ticket_ids: Array.from(selected) });
      toast.success(`${data.deleted} tiket dihapus`);
      load();
    } catch (e) {
      toast.error("Gagal menghapus massal");
    }
  };

  const openWa = (t) => {
    const num = toWaNumber(t.phone);
    if (!num) { toast.error("Nomor handphone tidak tersedia"); return; }
    const text = encodeURIComponent(`Halo ${t.nama}, kami menghubungi Anda terkait pengajuan ${t.ticket_no} (${t.topic}).`);
    window.open(`https://wa.me/${num}?text=${text}`, "_blank", "noopener");
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/tickets/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const range = dateFrom || dateTo ? `_${dateFrom || "awal"}_${dateTo || "akhir"}` : "";
      a.download = `tiket${range}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export berhasil");
    } catch (e) {
      toast.error("Gagal export");
    } finally {
      setExporting(false);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const allSelected = tickets.length > 0 && selected.size === tickets.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(tickets.map((t) => t.id)));

  const totalPages = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageTickets = tickets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Support</div>
          <h1 className="font-heading text-3xl text-white font-bold tracking-tight mt-1">Layanan & Keluhan</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola dan perbarui status pengajuan mahasiswa.</p>
        </div>
        <Button onClick={exportXlsx} disabled={exporting} data-testid="export-tickets-button"
          variant="outline" className="border-border bg-[#121212] text-white hover:bg-[#1C1C1E]">
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> {exporting ? "Mengekspor..." : "Export .xlsx"}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-[#121212] border-border p-4 rounded-md">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="font-mono text-2xl text-white mt-1">{stats.total}</div>
          </Card>
          <Card className="bg-[#121212] border-border p-4 rounded-md">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Headphones className="w-3 h-3" /> Layanan</div>
            <div className="font-mono text-2xl text-primary mt-1">{stats.layanan}</div>
          </Card>
          <Card className="bg-[#121212] border-border p-4 rounded-md">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquareWarning className="w-3 h-3" /> Keluhan</div>
            <div className="font-mono text-2xl text-[#FF9F0A] mt-1">{stats.keluhan}</div>
          </Card>
          <Card className="bg-[#121212] border-border p-4 rounded-md">
            <div className="text-xs text-muted-foreground">Baru</div>
            <div className="font-mono text-2xl text-[#0A84FF] mt-1">{stats.by_status?.Baru ?? 0}</div>
          </Card>
        </div>
      )}

      <Card className="bg-[#121212] border-border p-4 rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Cari</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="No. tiket, nama, NIM..." data-testid="ticket-search" className="pl-9 bg-[#0A0A0A] border-border h-9" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Jenis</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="ticket-type-filter" className="bg-[#0A0A0A] border-border h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="layanan">Layanan</SelectItem>
                <SelectItem value="keluhan">Keluhan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="ticket-status-filter" className="bg-[#0A0A0A] border-border h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Dari Tgl</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ticket-date-from" className="bg-[#0A0A0A] border-border h-9" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Sampai Tgl</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ticket-date-to" className="bg-[#0A0A0A] border-border h-9" />
          </div>
        </div>
      </Card>

      {isManager && selected.size > 0 && (
        <Card data-testid="ticket-bulk-bar" className="bg-primary/10 border-primary/30 p-3 rounded-md flex flex-wrap items-center gap-3">
          <span className="text-sm text-white font-medium">{selected.size} dipilih</span>
          <div className="flex-1 min-w-[180px] max-w-xs">
            <Select value={bulkAssignTo} onValueChange={setBulkAssignTo}>
              <SelectTrigger data-testid="bulk-assign-select" className="bg-[#0A0A0A] border-border h-9"><SelectValue placeholder="Tugaskan ke..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>— Lepas tugas —</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={bulkAssign} disabled={!bulkAssignTo} data-testid="bulk-assign-apply" className="bg-primary hover:bg-[#0056B3] h-9">
            <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Terapkan
          </Button>
          {isSuperAdmin && (
            <Button onClick={bulkDelete} data-testid="bulk-delete-apply" variant="outline" className="h-9 border-[#FF453A]/40 text-[#FF453A] hover:bg-[#FF453A]/10">
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Hapus
            </Button>
          )}
          <Button onClick={() => setSelected(new Set())} variant="ghost" className="h-9 text-muted-foreground hover:text-white">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </Card>
      )}

      <Card className="bg-[#121212] border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Memuat...</div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Belum ada pengajuan.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  {isManager && (
                    <th className="px-4 py-3 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="ticket-select-all" className="w-4 h-4 accent-primary" />
                    </th>
                  )}
                  <th className="text-left font-medium px-4 py-3">No. Tiket</th>
                  <th className="text-left font-medium px-4 py-3">Jenis / Topik</th>
                  <th className="text-left font-medium px-4 py-3">Mahasiswa</th>
                  <th className="text-left font-medium px-4 py-3">No. HP</th>
                  {customFields.map((f) => (
                    <th key={f.key} className="text-left font-medium px-4 py-3 whitespace-nowrap">{f.label}</th>
                  ))}
                  <th className="text-left font-medium px-4 py-3">Deskripsi</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  {isManager && <th className="text-left font-medium px-4 py-3">Ditugaskan</th>}
                  <th className="text-left font-medium px-4 py-3">Tanggapan</th>
                  <th className="text-left font-medium px-4 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pageTickets.map((t) => (
                  <tr key={t.id} data-testid={`ticket-row-${t.ticket_no}`} className="border-b border-border/50 hover:bg-[#1C1C1E]/50 align-top">
                    {isManager && (
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)} data-testid={`ticket-select-${t.ticket_no}`} className="w-4 h-4 accent-primary" />
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono text-primary whitespace-nowrap">{t.ticket_no}<div className="text-[10px] text-muted-foreground">{t.fill_date || (t.created_at || "").slice(0, 10)}</div></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs ${t.type === "layanan" ? "text-primary" : "text-[#FF9F0A]"}`}>
                        {t.type === "layanan" ? <Headphones className="w-3 h-3" /> : <MessageSquareWarning className="w-3 h-3" />}
                        {t.type}
                      </span>
                      <div className="text-white text-xs mt-0.5">{t.topic}</div>
                    </td>
                    <td className="px-4 py-3"><div className="text-white">{t.nama}</div><div className="text-[10px] text-muted-foreground">{t.nim} · {t.prodi || "-"}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap text-white/80 text-xs" data-testid={`ticket-phone-${t.ticket_no}`}>{t.phone || "-"}</td>
                    {customFields.map((f) => (
                      <td key={f.key} className="px-4 py-3 text-white/80 text-xs whitespace-nowrap" data-testid={`ticket-custom-${f.key}-${t.ticket_no}`}>{(t.custom && t.custom[f.key]) || "-"}</td>
                    ))}
                    <td className="px-4 py-3 max-w-xs"><div className="text-muted-foreground text-xs line-clamp-3">{t.description}</div></td>
                    <td className="px-4 py-3">
                      <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                        <SelectTrigger data-testid={`ticket-status-${t.ticket_no}`} className={`h-8 w-28 border ${STATUS_COLOR[t.status] || STATUS_COLOR.Baru}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    {isManager && (
                      <td className="px-4 py-3 min-w-[150px]">
                        <Select value={t.assigned_to || UNASSIGNED} onValueChange={(v) => assignOne(t, v)}>
                          <SelectTrigger data-testid={`ticket-assign-${t.ticket_no}`} className="h-8 bg-[#0A0A0A] border-border text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNASSIGNED}>Belum ditugaskan</SelectItem>
                            {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="px-4 py-3 min-w-[160px]">
                      <textarea
                        defaultValue={t.response_note || ""}
                        onBlur={(e) => saveNote(t, e.target.value)}
                        rows={2}
                        data-testid={`ticket-note-${t.ticket_no}`}
                        placeholder="Tulis tanggapan..."
                        className="w-full bg-[#0A0A0A] border border-border rounded-md p-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openWa(t)}
                          disabled={!t.phone}
                          data-testid={`ticket-wa-${t.ticket_no}`}
                          title={t.phone ? "Chat WhatsApp" : "Nomor tidak tersedia"}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.75} /> WA
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => deleteOne(t)}
                            data-testid={`ticket-delete-${t.ticket_no}`}
                            title="Hapus tiket"
                            className="inline-flex items-center px-2 py-1.5 rounded-md text-xs font-medium bg-[#FF453A]/10 text-[#FF453A] border border-[#FF453A]/30 hover:bg-[#FF453A]/20 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && tickets.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="ticket-pagination">
            <div className="text-xs text-muted-foreground">
              Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, tickets.length)} dari {tickets.length}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="ticket-page-prev" className="h-8 border-border bg-[#0A0A0A] text-white disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <span className="text-xs text-white font-mono">{currentPage} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="ticket-page-next" className="h-8 border-border bg-[#0A0A0A] text-white disabled:opacity-40">
                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
